/* global Swiper */

/** Tab panel visibility only — never remove Liquid `hidden` (variant image hiding). */
const ZOOM_TAB_HIDDEN_CLASS = 'product-zoom-modal__slide--inactive-tab';

if (!customElements.get('product-zoom-modal')) {
  customElements.define('product-zoom-modal', class ProductZoomModal extends HTMLElement {
    constructor() {
      super();
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
      this.handleFilterClick = this.handleFilterClick.bind(this);
      this.handleImageZoomMouseMove = this.handleImageZoomMouseMove.bind(this);
      this.handleFocusTrap = this.handleFocusTrap.bind(this);
      this.onCloseOrOverlayClick = () => this.hide();
      this.onThumbnailsClick = this.onThumbnailsClick.bind(this);
      this.activeFilter = 'image';
      this.imageCount = 0;
      this.videoCount = 0;
      this.updateImageCount = this.updateImageCount.bind(this);
      this.updateVideoCount = this.updateVideoCount.bind(this);
    }

    connectedCallback() {
      this.closeButton = this.querySelector('[data-zoom-close]');
      this.overlay = this.querySelector('[data-zoom-overlay]');
      this.thumbnailsContainer = this.querySelector('[data-zoom-thumbs]');
      this.mainContainer = this.querySelector('[data-zoom-main]');

      this.closeButton?.addEventListener('click', this.onCloseOrOverlayClick);
      this.overlay?.addEventListener('click', this.onCloseOrOverlayClick);
      this.bindThumbnailClicks();
      this.bindFilterClicks();
    }

    bindThumbnailClicks() {
      this.thumbnailsContainer?.addEventListener('click', this.onThumbnailsClick);
    }

    onThumbnailsClick(event) {
      const button = event.target.closest('.product-zoom-modal__thumbnail-button');
      if (!button || !this.thumbnailsContainer?.contains(button)) return;
      this.handleThumbnailClick(event, button);
    }

    unbindThumbnailClicks() {
      this.thumbnailsContainer?.removeEventListener('click', this.onThumbnailsClick);
    }

    bindFilterClicks() {
      this.querySelectorAll('[data-zoom-filter]').forEach((button) => {
        button.addEventListener('click', this.handleFilterClick);
      });
    }

    bindImageZoomMouseMove() {
      this.querySelectorAll('[data-zoom-main] [data-zoom-image]').forEach((image) => {
        image.addEventListener('mousemove', this.handleImageZoomMouseMove);
      });
    }

    unbindImageZoomMouseMove() {
      this.querySelectorAll('[data-zoom-main] [data-zoom-image]').forEach((image) => {
        image.removeEventListener('mousemove', this.handleImageZoomMouseMove);
      });
    }

    handleImageZoomMouseMove(event) {
      const host = event.currentTarget;
      const image = host.querySelector('.product-zoom-modal__main-image');
      if (!image) return;

      const rect = host.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      image.style.transformOrigin = `${x}% ${y}%`;
    }

    handleFilterClick(event) {
      event.preventDefault();
      event.stopPropagation();
      
      const button = event.currentTarget;
      const filter = button.dataset.filter;
      if (!filter) return;

      if (filter === 'image') {
        this.pauseAllMedia();
      }

      this.initialIndex = 0;
      this.activeFilter = filter;
      this.updateActiveFilter();
      this.updateActiveSlides();
      this.updateActiveThumbnail(this.initialIndex);
    }

    updateActiveFilter() {
      this.querySelectorAll('[data-zoom-filter]').forEach(button => {
        button.classList.toggle('product-zoom-modal__tab--active', button.dataset.filter === this.activeFilter);
      });
    }

    applyTabSlideSemantics() {
      this.querySelectorAll('[data-zoom-slides]').forEach((slide) => {
        if (slide.classList.contains('hidden')) {
          slide.classList.add('non-swiper-slide', ZOOM_TAB_HIDDEN_CLASS);
          slide.classList.remove('swiper-slide');
          slide.setAttribute('data-hidden', 'true');
          return;
        }
        if (slide.dataset.zoomSlides === this.activeFilter) {
          slide.classList.remove('non-swiper-slide', ZOOM_TAB_HIDDEN_CLASS);
          slide.classList.add('swiper-slide');
          slide.removeAttribute('data-hidden');
        } else {
          slide.classList.add('non-swiper-slide', ZOOM_TAB_HIDDEN_CLASS);
          slide.classList.remove('swiper-slide');
          slide.setAttribute('data-hidden', 'true');
        }
      });

      let thumbIdx = 0;
      this.querySelectorAll('[data-zoom-thumbs] [data-zoom-slides]').forEach((slide) => {
        if (slide.classList.contains('hidden')) {
          slide.removeAttribute('data-swiper-slide-index');
          return;
        }
        if (slide.dataset.zoomSlides === this.activeFilter) {
          slide.dataset.swiperSlideIndex = String(thumbIdx);
          thumbIdx += 1;
        } else {
          slide.removeAttribute('data-swiper-slide-index');
        }
      });
    }

    updateActiveSlides() {
      this.applyTabSlideSemantics();
      this.destroySwipers();
      this.initSwipers();
    }

    refreshAfterVariantMediaSync() {
      let nextIdx = this.initialIndex;
      if (this.mainSwiper) {
        nextIdx = this.mainSwiper.realIndex;
      }

      this.destroySwipers();
      this.applyTabSlideSemantics();

      const mainWrapper = this.querySelector('[data-zoom-main] .swiper-wrapper');
      const mainSlideCount = mainWrapper
        ? mainWrapper.querySelectorAll(':scope > .swiper-slide').length
        : 0;
      if (mainSlideCount > 0) {
        nextIdx = Math.min(nextIdx, mainSlideCount - 1);
      } else {
        nextIdx = 0;
      }
      this.initialIndex = nextIdx;

      this.updateImageCount();
      this.updateVideoCount();

      if (this.hasAttribute('open')) {
        this.initSwipers();
        this.updateSlideFocusability();
        this.updateActiveThumbnail(this.mainSwiper?.realIndex ?? this.initialIndex);
      }
    }

    updateImageCount() {
      if (!this.thumbnailsContainer) return;
      this.imageCount = this.thumbnailsContainer.querySelectorAll('[data-zoom-slides="image"]:not(.hidden)').length;
      const imageCountWrapper = this.querySelector('.js-product-zoom-modal-image-count');
      if (imageCountWrapper) {
        imageCountWrapper.textContent = `(${this.imageCount.toString()})`;
      }
    }

    updateVideoCount() {
      if (!this.thumbnailsContainer) return;
      this.videoCount = this.thumbnailsContainer.querySelectorAll('[data-zoom-slides="video"]:not(.hidden)').length;
      const videoCountWrapper = this.querySelector('.js-product-zoom-modal-video-count');
      if (videoCountWrapper) {
        videoCountWrapper.textContent = `(${this.videoCount.toString()})`;
      }
    }

    unbindFilterClicks() {
      this.querySelectorAll('[data-zoom-filter]').forEach((button) => {
        button.removeEventListener('click', this.handleFilterClick);
      });
    }

    disconnectedCallback() {
      this.closeButton?.removeEventListener('click', this.onCloseOrOverlayClick);
      this.overlay?.removeEventListener('click', this.onCloseOrOverlayClick);
      this.unbindThumbnailClicks();
      this.unbindFilterClicks();
      this.unbindImageZoomMouseMove();
      this.destroySwipers();
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('keydown', this.handleFocusTrap);
    }

    handleThumbnailClick(event, delegatedButton = null) {
      event.preventDefault();
      event.stopPropagation();

      const button = delegatedButton ?? event.currentTarget;
      if (!button) return;
      if (!this.mainSwiper) return;
      const slideIndex = Number.parseInt(button.dataset.swiperSlideIndex, 10);
      if (Number.isNaN(slideIndex)) return;

      if (this.mainSwiper.realIndex === slideIndex) return;

      this.pauseSlideMedia(this.mainSwiper.activeIndex);
      if (this.mainSwiper.params.loop) {
        this.mainSwiper.slideToLoop(slideIndex);
      } else {
        this.mainSwiper.slideTo(slideIndex);
      }
    }

    updateActiveThumbnail(realIndex) {
      if (!this.thumbnailsContainer) return;

      const thumbnails = this.thumbnailsContainer.querySelectorAll('[data-swiper-slide-index]');
      for (const thumb of thumbnails) {
        if (thumb.dataset.zoomSlides !== this.activeFilter) continue;

        const thumbIdx = Number.parseInt(thumb.dataset.swiperSlideIndex, 10);
        const isActive = !Number.isNaN(thumbIdx) && thumbIdx === realIndex;
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        thumb.classList.toggle('swiper-slide-thumb-active', isActive);
      }

      if (this.thumbsSwiper) {
        if (this.thumbsSwiper.params.loop) {
          this.thumbsSwiper.slideToLoop(realIndex);
        } else {
          this.thumbsSwiper.slideTo(realIndex);
        }
      }
    }

    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.hide();
        return;
      }
      if (event.key === 'ArrowLeft') {
        this.mainSwiper?.slidePrev();
        this.focusActiveThumbnail();
        return;
      }
      if (event.key === 'ArrowRight') {
        this.mainSwiper?.slideNext();
        this.focusActiveThumbnail();
      }
    }

    handleFocusTrap(event) {
      if (event.key !== 'Tab') return;

      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) return;

      const [first] = focusableElements;
      const last = focusableElements.slice().reverse().find(() => true);
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first?.focus();
      } else if (!focusableElements.includes(activeElement)) {
        event.preventDefault();
        first?.focus();
      }
    }

    isFocusTrapCandidate(element) {
      if (!(element instanceof HTMLElement)) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      return true;
    }

    getFocusableElements() {
      const elements = [];

      if (this.closeButton) {
        elements.push(this.closeButton);
      }

      this.querySelectorAll('[data-zoom-filter]').forEach((tab) => {
        elements.push(tab);
      });

      const prevBtn = this.querySelector('[data-zoom-main-prev]');
      const nextBtn = this.querySelector('[data-zoom-main-next]');
      if (prevBtn) elements.push(prevBtn);
      if (nextBtn) elements.push(nextBtn);

      const thumbnails = this.thumbnailsContainer?.querySelectorAll('[data-swiper-slide-index]');
      if (thumbnails) {
        for (const thumbnail of thumbnails) {
          if (thumbnail.dataset.zoomSlides !== this.activeFilter) continue;
          if (thumbnail.classList.contains('hidden')) continue;
          elements.push(thumbnail);
        }
      }

      const activeSlide = this.mainSwiper?.slides?.[this.mainSwiper.activeIndex];
      if (activeSlide) {
        const videoElements = activeSlide.querySelectorAll('video, iframe');
        for (const el of videoElements) {
          elements.push(el);
        }
      }

      return elements.filter((el) => this.isFocusTrapCandidate(el));
    }

    updateSlideFocusability() {
      if (!this.mainContainer) return;

      const slides = this.mainContainer.querySelectorAll('.swiper-slide');
      const activeIndex = this.mainSwiper?.activeIndex ?? 0;

      slides.forEach((slide, index) => {
        const focusableMedia = slide.querySelectorAll('video, iframe');
        const isActive = index === activeIndex;

        for (const el of focusableMedia) {
          el.setAttribute('tabindex', isActive ? '0' : '-1');
        }
      });
    }

    focusActiveThumbnail() {
      if (!this.mainSwiper || !this.thumbnailsContainer) return;
      
      const realIndex = this.mainSwiper.realIndex;
      const thumbnail = this.thumbnailsContainer.querySelector(
        `[data-swiper-slide-index="${realIndex}"][data-zoom-slides="${this.activeFilter}"]`
      );
      thumbnail?.focus();
    }

    show(initialIndex = 0, thumbRail = 'image') {
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('product-zoom-modal-open');
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('keydown', this.handleFocusTrap);
      this.activeFilter = thumbRail;
      this.initialIndex = initialIndex;
      this.updateActiveFilter();
      this.updateActiveSlides();
      this.updateSlideFocusability();
      this.updateActiveThumbnail(initialIndex);
      
      const initialThumbnail = this.thumbnailsContainer?.querySelector(
        `[data-swiper-slide-index="${initialIndex}"][data-zoom-slides="${thumbRail}"]`
      );
      if (initialThumbnail) {
        initialThumbnail.focus();
      } else {
        this.closeButton?.focus();
      }
    }

    hide() {
      this.pauseAllMedia();
      
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('product-zoom-modal-open');
      document.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('keydown', this.handleFocusTrap);
      
      this.destroySwipers();

      if (this.openedBy) {
        this.openedBy.focus();
        this.openedBy = null;
      }
    }

    pauseAllMedia() {
      for (const video of this.querySelectorAll('video')) {
        video.pause();
        video.currentTime = 0;
      }

      for (const slide of this.querySelectorAll('[data-zoom-main] [data-zoom-slides="video"]')) {
        this.ejectDeferredVideo(slide);
      }
    }

    pauseSlideMedia(activeSwiperSlideIndex) {
      if (!this.mainSwiper?.slides) return;

      const slide = this.mainSwiper.slides[activeSwiperSlideIndex];
      if (!slide) return;

      for (const video of slide.querySelectorAll('video')) {
        video.pause();
      }
    }

    injectDeferredVideo(slide) {
      const template = slide.querySelector('template[data-deferred-video]');
      if (!template) return;

      const wrapper = template.parentElement;
      if (wrapper.querySelector('iframe')) return;

      wrapper.appendChild(template.content.cloneNode(true));
      const iframe = wrapper.querySelector('iframe');
      iframe?.addEventListener('load', () => {
        iframe.setAttribute('data-loaded', '');
      }, { once: true });
    }

    ejectDeferredVideo(slide) {
      if (!slide.querySelector('template[data-deferred-video]')) return;

      const wrapper = slide.querySelector('.product-zoom-modal__video-wrapper');
      wrapper?.querySelector('iframe')?.remove();
    }

    manageSlideMedia() {
      if (!this.mainSwiper?.slides) return;

      const activeIndex = this.mainSwiper.activeIndex;
      for (const [index, slide] of [...this.mainSwiper.slides].entries()) {
        const isActive = index === activeIndex;
        if (isActive) {
          this.injectDeferredVideo(slide);
        } else {
          this.ejectDeferredVideo(slide);
        }

        for (const video of slide.querySelectorAll('video')) {
          if (!isActive) {
            video.pause();
          }
        }
      }
    }

    initSwipers() {
      const initSwiper = () => {
        if (typeof Swiper === 'undefined') return;

        const thumbsContainer = this.querySelector('[data-zoom-thumbs]');
        const mainContainer = this.querySelector('[data-zoom-main]');

        if (thumbsContainer) {
          this.thumbsSwiper = new Swiper(thumbsContainer, {
            slidesPerView: 'auto',
            spaceBetween: 10,
            freeMode: true,
            loop: false,
            watchSlidesProgress: true,
            initialSlide: this.initialIndex,
            allowTouchMove: true,
            mousewheel: {
              forceToAxis: true
            }
          });
        }

        if (mainContainer) {
          const mainWrapper = mainContainer.querySelector('.swiper-wrapper');
          const mainSlideCount = mainWrapper
            ? mainWrapper.querySelectorAll(':scope > .swiper-slide').length
            : 0;
          const mainLoop = mainSlideCount > 1;

          this.mainSwiper = new Swiper(mainContainer, {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: mainLoop,
            initialSlide: this.initialIndex,
            navigation: {
              nextEl: this.querySelector('[data-zoom-main-next]'),
              prevEl: this.querySelector('[data-zoom-main-prev]')
            },
            on: {
              slideChange: (swiper) => {
                this.initialIndex = swiper.realIndex;
                this.updateActiveThumbnail(swiper.realIndex);
                this.manageSlideMedia();
                this.updateSlideFocusability();
              }
            }
          });
        }

        this.manageSlideMedia();
        this.updateSlideFocusability();
        this.updateImageCount();
        this.updateVideoCount();

        this.unbindImageZoomMouseMove();
        this.bindImageZoomMouseMove();
      };

      if (typeof Swiper !== 'undefined') {
        initSwiper();
      } else {
        document.addEventListener('swiperReady', initSwiper, { once: true });
      }
    }

    destroySwipers() {
      this.mainSwiper?.destroy(true, true);
      this.thumbsSwiper?.destroy(true, true);
      this.mainSwiper = null;
      this.thumbsSwiper = null;
    }
  });
}

class ProductZoomOpener extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    event.preventDefault();

    const modalSelector = this.dataset.modal;
    const modal = document.querySelector(modalSelector);

    if (!modal) return;

    const slideIndexRaw = Number.parseInt(this.dataset.slideIndex, 10);
    const slideIndex = Number.isNaN(slideIndexRaw) ? 0 : slideIndexRaw;
    const thumbRail = this.dataset.thumbRail === 'video' ? 'video' : 'image';
    modal.openedBy = this;
    modal.show(slideIndex, thumbRail);
  }
}

if (!customElements.get('product-zoom-opener')) {
  customElements.define('product-zoom-opener', ProductZoomOpener);
}
